import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ExpandLess,
  ExpandMore,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  History as HistoryIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useLanguage } from '../i18n/LanguageContext';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface ReportItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const ExpandableReportsSidebar: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const privileges = useSelector((s: RootState) => s.auth.user?.privileges);
  const canViewReports = privileges === undefined || privileges.includes('reports:read');

  const handleClick = () => {
    setExpanded(!expanded);
  };

  const handleReportClick = (path: string) => {
    navigate(path);
  };

  const reportItems: ReportItem[] = [
    { text: t('reports.applicationReports'), icon: <DescriptionIcon />, path: '/reports?tab=0' },
    { text: t('reports.systemReports'), icon: <AssessmentIcon />, path: '/reports?tab=1' },
    { text: t('reports.visualizations'), icon: <BarChartIcon />, path: '/reports?tab=2' },
    { text: t('reports.portfolioHealth'), icon: <TrendingUpIcon />, path: '/reports?tab=3' },
    { text: t('reports.transactionReports'), icon: <ReceiptIcon />, path: '/reports?tab=4' },
    { text: t('reports.inactiveAlerts'), icon: <WarningIcon />, path: '/reports?tab=5' },
    { text: t('reports.accountLinking'), icon: <LinkIcon />, path: '/reports?tab=6' },
    { text: t('reports.loanHistory'), icon: <HistoryIcon />, path: '/reports?tab=7' },
    { text: t('reports.regulatoryReports'), icon: <GavelIcon />, path: '/reports?tab=8' },
    { text: t('reports.kycReports'), icon: <PersonIcon />, path: '/reports?tab=9' },
    { text: t('reports.advancedRisk'), icon: <SecurityIcon />, path: '/reports?tab=10' },
  ];

  if (!canViewReports) return null;

  return (
    <Box>
      <ListItem disablePadding sx={{ mb: 0.5, px: 1 }}>
        <ListItemButton
          onClick={handleClick}
          sx={{
            borderRadius: 2,
            color: 'rgba(255,255,255,0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: 'white',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('nav.reports')} 
            sx={{ '& .MuiTypography-root': { fontSize: '0.9rem', fontWeight: 'inherit' } }} 
          />
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ 
          pl: 2,
          background: 'transparent'
        }}>
          {reportItems.map((item, index) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5, px: 1 }}>
              <ListItemButton
                onClick={() => handleReportClick(item.path)}
                sx={{
                  borderRadius: 2,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    color: 'white',
                  },
                  '&.active': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: 500,
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    }
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{ '& .MuiTypography-root': { fontSize: '0.85rem', fontWeight: 'inherit' } }} 
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default ExpandableReportsSidebar;
